import { PageHeader } from '@/platform/design-system/components/ui/PageHeader';
/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/list-page/ListPage.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/list-page/ListPage.tsx
 * filename: ListPage.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * ListPage — shared primitive for list/table pages.
 *
 * Replaces the ad-hoc "KPI strip + wide table + inline filters" layout that
 * the UX audit (docs/ux-usability-audit-2026-04-07.md) flagged as the root
 * cause of info overload. A ListPage gives you:
 *
 *   - One header row (title + subtitle + primary action + ≤N secondary actions)
 *   - One optional collapsed summary line (NOT a KPI card strip)
 *   - One search box + one visible primary filter + a "More filters" popover
 *   - A column chooser persisted per-user via localStorage
 *   - A slot for the table (usually DataTable) via render-prop or children
 *
 * Row-click handling and the detail drawer itself are the page's concern —
 * re-export `DetailDrawer*` from this folder's index for consistency.
 */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { ColumnDef } from '../ui/DataTable';

export interface ListPageAction {
  label: string;
  icon?: React.ElementType;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export interface ListPageFilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface ListPageColumnConfig<T> {
  /** Every column the page can show. */
  all: ColumnDef<T>[];
  /** Keys visible by default (aim for ≤4). */
  defaultVisible: string[];
  /** Unique key used for localStorage persistence, e.g. "huntress". */
  storageKey: string;
}

/**
 * Visual tone for a tier card. Colors only apply when the card has a
 * non-zero numeric value — a `bad` card with value 0 is muted so the page
 * looks calm when it's calm.
 */
export type TierTone = 'good' | 'bad' | 'warn' | 'info' | 'neutral';

export interface TierCard {
  /** Unique key — also used as the filter key when the card is clicked. */
  key: string;
  value: number | string;
  label: string;
  sublabel?: string;
  tone?: TierTone;
}

export interface TierHero extends TierCard {
  /**
   * Cards that appear in place of the medium row when the hero is clicked.
   * This is the "drill sideways" — composition of the hero number, not
   * repetition of the problem cards. Keep it to 2–4 cards.
   */
  expand?: TierCard[];
}

export interface ListPageTiers {
  hero: TierHero;
  /** Exactly 2 medium cards recommended — the problems you'd check after the hero. */
  medium: TierCard[];
  /** Parent-owned filter state. `null` means "no filter". */
  activeFilter: string | null;
  /** Called with a new filter key, or `null` to clear. */
  onFilterChange: (key: string | null) => void;
}

export interface ListPageProps<T> {
  title: string;
  subtitle?: string;
  icon?: React.ElementType;
  iconColorClass?: string;

  /**
   * Optional slot rendered above the header row. Use for cluster-level
   * navigation (e.g. PageTabs strip shared across sibling pages). Goes
   * inside the page padding so it lines up with the header.
   */
  tabsAbove?: ReactNode;

  primaryAction?: ListPageAction;
  secondaryActions?: ListPageAction[];

  /**
   * Tiered card layout: one hero + two medium cards, with click-to-expand
   * drill-down that replaces the medium row with 2–4 contextual small cards.
   * Cards are also click-to-filter — the hero exploration and filter state
   * are independent (clicking a medium/expand card toggles the filter;
   * clicking the hero toggles expansion and does NOT touch the filter).
   *
   * When `tiers` is set, the `summary` and `primaryFilter` props are ignored.
   */
  tiers?: ListPageTiers;

  /**
   * One-line summary replacing the KPI strip. Plain text or nodes.
   * Legacy fallback when `tiers` isn't supplied.
   */
  summary?: ReactNode;

  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
  };

  primaryFilter?: {
    value: string;
    onChange: (v: string) => void;
    options: ListPageFilterOption[];
    label?: string;
  };

  /** Rendered inside the "More filters" popover. */
  extraFilters?: ReactNode;

  columns?: ListPageColumnConfig<T>;

  /**
   * Content slot. If `columns` is provided, use the function form to receive
   * the filtered visible columns. Otherwise pass plain children.
   */
  children: ReactNode | ((ctx: { visibleColumns: ColumnDef<T>[] }) => ReactNode);
}

export function ListPage<T>(props: ListPageProps<T>) {
  const {
    title,
    subtitle,
    icon: Icon,
    iconColorClass = 'text-emerald-500',
    tabsAbove,
    primaryAction,
    secondaryActions = [],
    tiers,
    summary,
    search,
    primaryFilter,
    extraFilters,
    columns,
    children,
  } = props;

  // When tiers drive the filter, the legacy primaryFilter dropdown is hidden.
  const tiersOwnFilter = !!tiers;
  const effectivePrimaryFilter = tiersOwnFilter ? undefined : primaryFilter;

  const storageKey = columns ? `listpage.cols.${columns.storageKey}` : null;

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    if (!columns || !storageKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter(
            (k): k is string => typeof k === 'string' && columns.all.some((c) => c.key === k)
          );
          if (filtered.length > 0) return filtered;
        }
      }
    } catch {
      // ignore — fall through to defaults
    }
    return columns.defaultVisible;
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
    } catch {
      // ignore write failures (quota, privacy mode, etc.)
    }
  }, [visibleKeys, storageKey]);

  const visibleColumns = useMemo(() => {
    if (!columns) return [] as ColumnDef<T>[];
    // Preserve the order declared in `columns.all`, not the order the user
    // toggled them on — keeps the table predictable.
    return columns.all.filter((c) => visibleKeys.includes(c.key));
  }, [columns, visibleKeys]);

  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);

  const hasControls = Boolean(search || effectivePrimaryFilter || extraFilters || columns);

  return (
    <div className="p-6">
      {tabsAbove && <div className="mb-4">{tabsAbove}</div>}
      {/* Header row */}
      <PageHeader
        title={title}
        subtitle={subtitle}
        titleBadge={Icon && <Icon className={clsx('w-7 h-7', iconColorClass)} />}
        actions={
          <>
            {secondaryActions.map((a, i) => (
              <ActionButton key={i} action={a} variant="secondary" />
            ))}
            {primaryAction && <ActionButton action={primaryAction} variant="primary" />}
          </>
        }
      />

      {/* Tiered cards (preferred) or collapsed summary line (legacy) */}
      {tiers ? (
        <TiersBlock tiers={tiers} />
      ) : (
        summary && (
          <div className="mb-4 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
            {summary}
          </div>
        )
      )}

      {/* Controls row: search + primary filter + more filters + columns chooser */}
      {hasControls && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {search && (
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={search.placeholder ?? 'Search...'}
                aria-label={search.placeholder ?? 'Search'}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          )}

          {effectivePrimaryFilter && (
            <select
              aria-label={effectivePrimaryFilter.label ?? 'Filter'}
              value={effectivePrimaryFilter.value}
              onChange={(e) => effectivePrimaryFilter.onChange(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {effectivePrimaryFilter.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                  {o.count != null ? ` (${o.count})` : ''}
                </option>
              ))}
            </select>
          )}

          {extraFilters && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                More filters
              </button>
              {showFilters && (
                <>
                  <button
                    type="button"
                    aria-label="Close filters"
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setShowFilters(false)}
                  />
                  <div className="absolute right-0 mt-2 w-80 z-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-4">
                    {extraFilters}
                  </div>
                </>
              )}
            </div>
          )}

          {columns && (
            <div className="relative ml-auto">
              <button
                type="button"
                onClick={() => setShowColumns((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Columns ({visibleKeys.length}/{columns.all.length})
                <ChevronDownIcon className="w-4 h-4" />
              </button>
              {showColumns && (
                <>
                  <button
                    type="button"
                    aria-label="Close column chooser"
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setShowColumns(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 z-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-2 max-h-80 overflow-y-auto">
                    {columns.all.map((col) => {
                      const checked = visibleKeys.includes(col.key);
                      const isLastVisible = checked && visibleKeys.length === 1;
                      return (
                        <label
                          key={col.key}
                          className={clsx(
                            'flex items-center gap-2 px-2 py-1.5 rounded text-sm text-gray-700 dark:text-gray-300',
                            isLastVisible
                              ? 'opacity-60 cursor-not-allowed'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isLastVisible}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setVisibleKeys((prev) =>
                                  prev.includes(col.key) ? prev : [...prev, col.key]
                                );
                              } else if (!isLastVisible) {
                                setVisibleKeys((prev) => prev.filter((k) => k !== col.key));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          {col.header}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content slot */}
      <div>
        {typeof children === 'function'
          ? (children as (ctx: { visibleColumns: ColumnDef<T>[] }) => ReactNode)({
              visibleColumns,
            })
          : children}
      </div>
    </div>
  );
}

/**
 * Tiers block — hero + medium row with click-to-expand drill-down.
 *
 * Layout (md+): a 4-column grid. Hero occupies the left 2 columns; the right
 * 2 columns hold either the two medium cards side-by-side OR, when the hero
 * is expanded, a 2-column grid of contextual small cards.
 *
 * Filter vs expand are independent:
 *   - Clicking the hero toggles expansion only. It never touches the filter.
 *   - Clicking a medium or small card toggles that card's filter key.
 *     Clicking the active filter card again clears it.
 *   - Expansion is not auto-collapsed by anything — it stays where the user
 *     put it until they click the hero again.
 */
function TiersBlock({ tiers }: { tiers: ListPageTiers }) {
  const { hero, medium, activeFilter, onFilterChange } = tiers;
  const [expanded, setExpanded] = useState(false);
  const hasExpand = !!hero.expand && hero.expand.length > 0;

  const handleCardClick = (key: string) => {
    onFilterChange(activeFilter === key ? null : key);
  };

  const isDimmed = (key: string) => activeFilter !== null && activeFilter !== key;

  return (
    <div className="mb-5 grid grid-cols-1 md:grid-cols-4 gap-3">
      {/* Hero */}
      <button
        type="button"
        onClick={() => hasExpand && setExpanded((v) => !v)}
        disabled={!hasExpand}
        className={clsx(
          'md:col-span-2 text-left rounded-xl border p-6 transition-all',
          toneClasses(hero.tone, hero.value),
          isDimmed(hero.key) ? 'opacity-50' : 'opacity-100',
          hasExpand
            ? 'cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-900'
            : 'cursor-default'
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium opacity-80">{hero.label}</div>
            <div className="mt-2 text-5xl font-bold tabular-nums leading-none">{hero.value}</div>
            {hero.sublabel && <div className="mt-2 text-xs opacity-70">{hero.sublabel}</div>}
          </div>
          {hasExpand && (
            <ChevronDownIcon
              className={clsx(
                'w-5 h-5 shrink-0 opacity-60 transition-transform duration-200',
                expanded && 'rotate-180'
              )}
            />
          )}
        </div>
      </button>

      {/* Right side: medium cards OR expanded small cards */}
      {expanded && hasExpand ? (
        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          {hero.expand!.map((card) => (
            <TierCardView
              key={card.key}
              card={card}
              size="small"
              active={activeFilter === card.key}
              dimmed={isDimmed(card.key)}
              onClick={() => handleCardClick(card.key)}
            />
          ))}
        </div>
      ) : (
        medium.map((card) => (
          <TierCardView
            key={card.key}
            card={card}
            size="medium"
            active={activeFilter === card.key}
            dimmed={isDimmed(card.key)}
            onClick={() => handleCardClick(card.key)}
          />
        ))
      )}
    </div>
  );
}

function TierCardView({
  card,
  size,
  active,
  dimmed,
  onClick,
}: {
  card: TierCard;
  size: 'medium' | 'small';
  active: boolean;
  dimmed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'text-left rounded-xl border transition-all cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-900',
        toneClasses(card.tone, card.value),
        dimmed ? 'opacity-50' : 'opacity-100',
        active && 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-900',
        active && activeRingTone(card.tone, card.value),
        size === 'medium' ? 'p-5' : 'p-4'
      )}
    >
      <div className={clsx('font-medium opacity-80', size === 'medium' ? 'text-sm' : 'text-xs')}>
        {card.label}
      </div>
      <div
        className={clsx(
          'mt-1 font-bold tabular-nums leading-none',
          size === 'medium' ? 'text-3xl' : 'text-2xl'
        )}
      >
        {card.value}
      </div>
      {card.sublabel && <div className="mt-2 text-xs opacity-70">{card.sublabel}</div>}
    </button>
  );
}

/**
 * Tone classes pick a background + border + text color. Tones `bad` and
 * `warn` only apply color when the card has a non-zero numeric value — a
 * "0 offline" card should look calm, not red.
 */
function toneClasses(tone: TierTone | undefined, value: number | string): string {
  const hasValue = typeof value === 'number' ? value > 0 : Boolean(value) && value !== '0';

  if (tone === 'bad' && hasValue) {
    return 'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-800/60 dark:text-red-50';
  }
  if (tone === 'warn' && hasValue) {
    return 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800/60 dark:text-amber-50';
  }
  if (tone === 'good') {
    return 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800/60 dark:text-emerald-50';
  }
  if (tone === 'info') {
    return 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800/60 dark:text-blue-50';
  }
  // neutral, or bad/warn with zero value — stay calm
  return 'bg-white border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white';
}

function activeRingTone(tone: TierTone | undefined, value: number | string): string {
  const hasValue = typeof value === 'number' ? value > 0 : Boolean(value) && value !== '0';
  if (tone === 'bad' && hasValue) return 'ring-red-500';
  if (tone === 'warn' && hasValue) return 'ring-amber-500';
  if (tone === 'good') return 'ring-emerald-500';
  if (tone === 'info') return 'ring-accent-ring';
  return 'ring-gray-400 dark:ring-gray-500';
}

function ActionButton({
  action,
  variant,
}: {
  action: ListPageAction;
  variant: 'primary' | 'secondary';
}) {
  const Icon = action.icon;
  const base =
    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-80 disabled:cursor-not-allowed transition-colors';
  const cls =
    variant === 'primary'
      ? 'text-white bg-emerald-700 hover:bg-emerald-800'
      : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled || action.loading}
      className={clsx(base, cls)}
    >
      {Icon && <Icon className={clsx('w-4 h-4', action.loading && 'motion-loading-spin')} />}
      {action.label}
    </button>
  );
}
